import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useContentHistoryByProfile } from '../hooks/useContent';
import { useBusinessProfiles } from '../hooks/useBusinessProfiles';
import { useAllPlatforms, useContentPurposes } from '../hooks/useCommon';
import { useUserAvailablePlatforms } from '../hooks/usePlanEntitlements';
import { useDeleteContentGeneration } from '../hooks/useContent';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import PageSection from '../components/PageSection';
import HistoryCard from './history/HistoryCard';

function GenerationHistory() {
  const { t } = useTranslation('content');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedPurpose, setSelectedPurpose] = useState('');
  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});
  const [expandedGenerations, setExpandedGenerations] = useState<{ [key: number]: boolean }>({});
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [itemToArchive, setItemToArchive] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isArchivedView, setIsArchivedView] = useState(false);
  const limit = 10; // Items per page

  const { data: businessProfiles, isLoading: loadingProfiles } = useBusinessProfiles();
  
  // Create a map of business profile ID to business profile object for quick lookup
  const businessProfileMap = useMemo(() => {
    if (!businessProfiles) return {};
    const map: Record<number, any> = {};
    businessProfiles.forEach((profile: any) => {
      map[profile.id] = profile;
    });
    return map;
  }, [businessProfiles]);
  
  // Hide Business Profile filter for normal users (client/user roles)
  const showBusinessProfileFilter = user?.role !== 'client' && user?.role !== 'user';
  const { data: platforms, isLoading: loadingPlatforms } = useAllPlatforms();
  const { data: availablePlatforms, isLoading: loadingAvailablePlatforms } = useUserAvailablePlatforms();
  const { data: contentPurposes, isLoading: loadingPurposes } = useContentPurposes();
  
  // Create platform name to display_name mapping
  const platformDisplayNameMap = useMemo(() => {
    if (!platforms) return {};
    const map: Record<string, string> = {};
    platforms.forEach((platform: any) => {
      map[platform.name] = platform.display_name || platform.name;
    });
    return map;
  }, [platforms]);
  
  // Get available platform IDs for filtering
  const availablePlatformIds = useMemo(() => {
    if (!availablePlatforms) return new Set<number>();
    return new Set((availablePlatforms || []).map((p: any) => p.id));
  }, [availablePlatforms]);
  
  // Get history for selected profile or first profile if none selected
  const activeProfileId = selectedProfile ? Number(selectedProfile) : (businessProfiles?.[0]?.id);
  const { data: historyResponse, isLoading: loadingHistory, refetch } = useContentHistoryByProfile(
    activeProfileId?.toString(),
    {
      page: currentPage,
      limit: limit,
      isArchived: isArchivedView,
      refetchOnWindowFocus: true,
      refetchInterval: 30000, // Auto-refresh every 30 seconds
    }
  );

  // Extract history data and pagination info
  const history = historyResponse?.data || [];
  const pagination = historyResponse?.pagination || {
    totalCount: 0,
    currentPage: 1,
    perPage: limit,
    totalPages: 0,
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProfile, selectedPlatform, selectedPurpose, isArchivedView, searchTerm]);

  // Auto-refresh when profile changes
  useEffect(() => {
    if (activeProfileId) {
      refetch();
    }
  }, [selectedProfile, activeProfileId, refetch]);

  // Get platforms available to the user (filtered by their plan entitlements)
  const availablePlatformsForFilter = useMemo(() => {
    if (!platforms || !availablePlatforms) return [];
    
    const enabledPlatforms = platforms.filter((platform: any) => 
      availablePlatformIds.has(platform.id)
    );
    
    return enabledPlatforms
      .map((platform: any) => platform.display_name || platform.name)
      .sort();
  }, [platforms, availablePlatforms, availablePlatformIds]);

  // Get all available purposes from API
  const availablePurposesForFilter = useMemo(() => {
    if (!contentPurposes) return [];
    
    return contentPurposes
      .map((purpose: any) => ({
        name: purpose.name,
        display_name: purpose.display_name || purpose.name,
      }))
      .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name));
  }, [contentPurposes]);
  
  // Create a mapping from display_name to name for filtering
  const purposeDisplayNameToNameMap = useMemo(() => {
    if (!contentPurposes) return {};
    const map: Record<string, string> = {};
    contentPurposes.forEach((purpose: any) => {
      const displayName = purpose.display_name || purpose.name;
      map[displayName] = purpose.name;
    });
    return map;
  }, [contentPurposes]);
  
  // Create a mapping from purpose name to display_name for display
  const purposeNameToDisplayNameMap = useMemo(() => {
    if (!contentPurposes) return {};
    const map: Record<string, string> = {};
    contentPurposes.forEach((purpose: any) => {
      map[purpose.name] = purpose.display_name || purpose.name;
    });
    return map;
  }, [contentPurposes]);

  // Filter history based on search and filters
  const filteredHistory = useMemo(() => {
    if (!history) return [];
    
    return history.filter((item: any) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          item.business_profile?.business_name?.toLowerCase().includes(searchLower) ||
          item.content_purpose?.toLowerCase().includes(searchLower) ||
          item.topic?.toLowerCase().includes(searchLower) ||
          JSON.stringify(item.generated_content).toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Platform filter
      if (selectedPlatform) {
        const hasPlatform = item.generated_content && 
          typeof item.generated_content === 'object' &&
          Object.keys(item.generated_content).some((platformName: string) => {
            const displayName = platformDisplayNameMap[platformName] || platformName;
            return displayName === selectedPlatform;
          });
        if (!hasPlatform) return false;
      }

      // Purpose filter
      if (selectedPurpose) {
        const purposeName = purposeDisplayNameToNameMap[selectedPurpose] || selectedPurpose;
        
        let itemPurposeName: string | null = null;
        
        if (typeof item.content_purpose === 'string') {
          itemPurposeName = item.content_purpose;
        } else if (item.content_purpose && typeof item.content_purpose === 'object') {
          itemPurposeName = item.content_purpose.name || item.content_purpose.display_name;
        } else if (item.generation_metadata && item.generation_metadata.content_purpose) {
          itemPurposeName = item.generation_metadata.content_purpose;
        }
        
        if (itemPurposeName !== purposeName) {
          return false;
        }
      }

      return true;
    });
  }, [history, searchTerm, selectedPlatform, selectedPurpose, platformDisplayNameMap, purposeDisplayNameToNameMap]);

  // Auto-expand the first item only on page 1 when history loads or changes
  useEffect(() => {
    if (filteredHistory && filteredHistory.length > 0 && currentPage === 1) {
      const firstItemId = filteredHistory[0].id;
      setExpandedGenerations(prev => ({
        ...prev,
        [firstItemId]: true
      }));
    }
  }, [filteredHistory, currentPage]);

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      // console.error('Failed to copy text: ', err);
    }
  };

  const archiveContentGeneration = useDeleteContentGeneration();

  const handleArchive = (id: number) => {
    setItemToArchive(id);
    setArchiveDialogOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (itemToArchive) {
      try {
        await archiveContentGeneration.mutateAsync(itemToArchive.toString());
        setArchiveDialogOpen(false);
        setItemToArchive(null);
      } catch (error) {
        // Error is handled by the mutation hook (toast notification)
      }
    }
  };

  const handleCancelArchive = () => {
    setArchiveDialogOpen(false);
    setItemToArchive(null);
  };

  const handleView = (id: number) => {
    setExpandedGenerations(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Check if any generation is expanded (drives the toggle button label and direction)
  // Using "any expanded" instead of "all expanded" so that when the first item is
  // auto-expanded on load, the button correctly shows "Collapse All" and collapses
  // everything in a single click instead of requiring two clicks.
  const anyExpanded = useMemo(() => {
    if (!filteredHistory || filteredHistory.length === 0) return false;
    return filteredHistory.some((item: any) => expandedGenerations[item.id]);
  }, [filteredHistory, expandedGenerations]);

  // Toggle expand/collapse all generations
  const handleExpandCollapseAll = () => {
    if (!filteredHistory) return;

    if (anyExpanded) {
      // At least one is open → collapse everything
      setExpandedGenerations({});
    } else {
      // Nothing is open → expand everything
      const newState: { [key: number]: boolean } = {};
      filteredHistory.forEach((item: any) => {
        newState[item.id] = true;
      });
      setExpandedGenerations(newState);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProfile('');
    setSelectedPlatform('');
    setSelectedPurpose('');
    setCurrentPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArchiveViewToggle = (_event: React.MouseEvent<HTMLElement>, newView: boolean | null) => {
    if (newView !== null) {
      setIsArchivedView(newView);
      setCurrentPage(1);
    }
  };

  if (loadingProfiles) {
    return (
      <PageSection
        title={t('history.title')}
        subtitle={t('history.subtitle')}
        loading={true}
        loadingText={t('history.loadingProfiles')}
        hideTitleOnMobile
      >
        <div />
      </PageSection>
    );
  }

  if (!businessProfiles || businessProfiles.length === 0) {
    return (
      <PageSection title={t('history.title')} subtitle={t('history.noProfilesFound')} hideTitleOnMobile>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('history.noProfilesFound')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {t('history.noProfilesFoundDesc')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/business-profiles')}
            sx={{ mt: 2 }}
          >
            {t('generation.createBusinessProfile')}
          </Button>
        </Box>
      </PageSection>
    );
  }

  return (
    <PageSection
      title={t('history.title')}
      subtitle={t('history.subtitle')}
      loading={loadingHistory}
      loadingText={t('history.loading')}
      hideTitleOnMobile
    >
      {/* Filters */}
      <Box sx={{ mb: 4, width: '100%' }}>
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center" 
          sx={{ 
            width: '100%', 
            mb: 3, 
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <TextField
            placeholder={t('history.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ flex: 1, minWidth: 250, '& .MuiInputBase-root': { height: '50px' } }}
          />
          {showBusinessProfileFilter && (
            <FormControl sx={{ 
              width: 200, 
              flexShrink: 0, 
              '& .MuiInputBase-root': { 
                height: '50px',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiInputLabel-root': {
                transform: 'translate(14px, 50%) scale(1)',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
              },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                paddingTop: 0,
                paddingBottom: 0,
              },
            }} size="small">
              <InputLabel>{t('history.filters.businessProfile')}</InputLabel>
              <Select
                value={selectedProfile || ''}
                onChange={(e) => setSelectedProfile(e.target.value)}
                label={t('history.filters.businessProfile')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                <MenuItem value="">{t('history.filters.allProfiles')}</MenuItem>
                {businessProfiles?.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.business_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl sx={{ 
            width: 180, 
            flexShrink: 0, 
            '& .MuiInputBase-root': { 
              height: '50px',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 50%) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
              },
            },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              paddingTop: 0,
              paddingBottom: 0,
            },
          }} size="small">
            <InputLabel>{t('history.filters.platform')}</InputLabel>
            <Select
              value={selectedPlatform || ''}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              label={t('history.filters.platform')}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
            >
              <MenuItem value="">{t('history.filters.allPlatforms')}</MenuItem>
              {availablePlatformsForFilter.map((platform) => (
                <MenuItem key={platform} value={platform}>
                  {platform}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ 
            width: 180, 
            flexShrink: 0, 
            '& .MuiInputBase-root': { 
              height: '50px',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 50%) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
              },
            },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              paddingTop: 0,
              paddingBottom: 0,
            },
          }} size="small">
            <InputLabel>{t('history.filters.purpose')}</InputLabel>
            <Select
              value={selectedPurpose || ''}
              onChange={(e) => setSelectedPurpose(e.target.value)}
              label={t('history.filters.purpose')}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
            >
              <MenuItem value="">{t('history.filters.allPurposes')}</MenuItem>
              {availablePurposesForFilter.map((purpose) => (
                <MenuItem key={purpose.name} value={purpose.display_name}>
                  {purpose.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterIcon />}
            onClick={clearFilters}
            disabled={!searchTerm && !selectedPlatform && !selectedPurpose && (!selectedProfile || selectedProfile === '')}
            sx={{ 
              flexShrink: 0,
              height: '50px',
              borderColor: '#6C63FF',
              color: '#6C63FF',
              '&:hover': {
                borderColor: '#5a52ee',
                backgroundColor: 'rgba(108, 99, 255, 0.04)',
              },
              '&:disabled': {
                borderColor: 'rgba(0, 0, 0, 0.26)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            {t('history.filters.clearFilters')}
          </Button>
          <ToggleButtonGroup
            value={isArchivedView}
            exclusive
            onChange={handleArchiveViewToggle}
            aria-label="view type"
            sx={{ flexShrink: 0, height: '50px' }}
          >
            <ToggleButton value={false} aria-label="active items">
              {t('history.views.active')}
            </ToggleButton>
            <ToggleButton value={true} aria-label="archived items" sx={{ color: '#6C63FF' }}>
              <ArchiveIcon sx={{ mr: 0.5, fontSize: 18 }} />
              {t('history.views.archive')}
            </ToggleButton>
          </ToggleButtonGroup>
          {filteredHistory && filteredHistory.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={anyExpanded ? <CollapseAllIcon /> : <ExpandAllIcon />}
              onClick={handleExpandCollapseAll}
              sx={{ flexShrink: 0, height: '50px' }}
            >
              {anyExpanded ? t('history.actions.collapseAll') : t('history.actions.expandAll')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Results */}
      {filteredHistory.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {isArchivedView ? t('history.emptyArchived') : t('history.empty')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {searchTerm || selectedPlatform || selectedPurpose 
              ? t('history.emptyFilterDesc')
              : isArchivedView
              ? t('history.emptyArchivedDesc')
              : t('history.emptyDesc')
            }
          </Typography>
          {!searchTerm && !selectedPlatform && !selectedPurpose && !isArchivedView && (
            <Button
              variant="contained"
              onClick={() => navigate('/generate')}
              sx={{ mt: 2 }}
            >
              {t('history.generateNow')}
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {pagination.totalCount > 0 ? (
              <>
                {t('history.showing')} {((currentPage - 1) * limit) + 1}-
                {Math.min(currentPage * limit, pagination.totalCount)} {t('history.of')} {pagination.totalCount} {pagination.totalCount === 1 ? t('history.item') : t('history.items')}
                {isArchivedView && ` (${t('history.archived')})`}
              </>
            ) : (
              `${t('history.empty')}`
            )}
          </Typography>
          <Stack spacing={2}>
            {filteredHistory.map((item: any) => (
              <HistoryCard
                key={item.id}
                item={item}
                copiedStates={copiedStates}
                onCopy={handleCopy}
                onArchive={handleArchive}
                onView={handleView}
                platformDisplayNameMap={platformDisplayNameMap}
                arePlatformsExpanded={expandedGenerations[item.id] || false}
                businessProfileMap={businessProfileMap}
                purposeNameToDisplayNameMap={purposeNameToDisplayNameMap}
              />
            ))}
          </Stack>
          
          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}

      {/* Archive Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={archiveDialogOpen}
        onClose={handleCancelArchive}
        onConfirm={handleConfirmArchive}
        title={t('history.archiveDialog.title')}
        itemType="content generation"
        questionText={t('history.archiveDialog.question')}
        description={t('history.archiveDialog.description')}
        warningTitle={t('history.archiveDialog.warningTitle')}
        warningMessage={t('history.archiveDialog.warning')}
        isDeleting={archiveContentGeneration.isPending}
        confirmButtonText={t('history.archiveDialog.confirm')}
        cancelButtonText={t('history.archiveDialog.cancel')}
      />
    </PageSection>
  );
}

export default GenerationHistory;
